import * as Tone from 'tone';
import { Midi } from '@tonejs/midi';

const BPM = 90;

class DeathMusic {
    constructor() {
        this.square1Pool = Array(4).fill(null).map(() => new Tone.Synth({
            oscillator: { type: 'square', width: 0.5 },
            envelope: { attack: 0.01, decay: 0.1, sustain: 0.3, release: 0.1 }
        }).toDestination());
        this.square1Pool.forEach(s => s.volume.value = -32);

        this.square2Pool = Array(4).fill(null).map(() => new Tone.Synth({
            oscillator: { type: 'square', width: 0.5 },
            envelope: { attack: 0.01, decay: 0.1, sustain: 0.3, release: 0.1 }
        }).toDestination());
        this.square2Pool.forEach(s => s.volume.value = -32);

        this.trianglePool = Array(4).fill(null).map(() => new Tone.Synth({
            oscillator: { type: 'triangle' },
            envelope: { attack: 0.01, decay: 0.1, sustain: 0.5, release: 0.1 }
        }).toDestination());
        this.trianglePool.forEach(s => s.volume.value = -13);

        this.sq1i = 0; this.sq2i = 0; this.trii = 0;

        this.transport = Tone.getTransport();
        this.parts = [];
        this.initialized = false;
    }

    next(pool, prop) {
        const s = pool[this[prop]];
        this[prop] = (this[prop] + 1) % pool.length;
        return s;
    }

    async init(midiFiles) {
        if (this.initialized) return;
        await Tone.start();

        let masterPPQ = 0, maxTicks = 0;
        for (const file of Object.values(midiFiles)) {
            if (!file) continue;
            const m = new Midi(file);
            if (!masterPPQ) { masterPPQ = m.header.ppq; Tone.Transport.PPQ = masterPPQ; }
            maxTicks = Math.max(maxTicks, m.durationTicks);
        }

        this.songEnd = `${Math.ceil(maxTicks / (4 * masterPPQ)) * 4 * masterPPQ}i`;

        const schedule = (midiData, getNote) => {
            if (!midiData) return;
            const notes = new Midi(midiData).tracks[0]?.notes ?? [];
            const part = new Tone.Part((time, n) => {
                try { getNote(n, time); } catch (e) {}
            }, notes.map(n => ({ time: `${n.ticks}i`, note: n.name, duration: `${n.durationTicks}i`, velocity: n.velocity })));
            part.loop = false;
            part.start(0);
            this.parts.push(part);
        };

        schedule(midiFiles.mel1, (n, t) => this.next(this.square1Pool,  'sq1i').triggerAttackRelease(n.note, n.duration, t, n.velocity));
        schedule(midiFiles.mel2, (n, t) => this.next(this.square2Pool,  'sq2i').triggerAttackRelease(n.note, n.duration, t, n.velocity));
        schedule(midiFiles.bass, (n, t) => this.next(this.trianglePool, 'trii').triggerAttackRelease(n.note, n.duration, t, n.velocity));

        this.initialized = true;
    }

    play(onEnd) {
        this.transport.stop();
        this.transport.position = 0;
        this.transport.bpm.value = BPM;
        if (onEnd && this.songEnd) {
            this.transport.scheduleOnce(() => onEnd(), this.songEnd);
        }
        this.transport.start('+0.1');
    }

    stop() {
        this.parts.forEach(p => { p.stop(); p.dispose(); });
        this.parts = [];
        this.transport.stop();
        this.transport.position = 0;
        this.initialized = false; // force reinit next death
    }

    dispose() {
        this.parts.forEach(p => p.dispose());
        [...this.square1Pool, ...this.square2Pool, ...this.trianglePool]
            .forEach(s => s.dispose());
        this.initialized = false;
    }
}

export default DeathMusic;
